import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { suppliersService, beverageProductsService } from '../services';
import { useSettings } from '../contexts/SettingsContext';
import ProductPicker from '../components/ProductPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
  Plus, Search, Loader2, Save, Trash2, Pencil, Truck, Package, X, ArrowLeft, Link2
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

export default function SuppliersPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currencySymbol } = useSettings();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [form, setForm] = useState({
    supplier_name: '', supplier_code: '', contact_name: '',
    contact_email: '', contact_phone: '', address: '', notes: '',
  });

  // Product linking state
  const [allProducts, setAllProducts] = useState([]);
  const [productLinks, setProductLinks] = useState([]);
  const [linkingProduct, setLinkingProduct] = useState('');
  const [linkCostPrice, setLinkCostPrice] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const [removingLink, setRemovingLink] = useState(null);

  // Load all products once for the picker
  useEffect(() => {
    beverageProductsService.listAll({ status: 'active' }).then(setAllProducts).catch(() => {});
  }, []);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await suppliersService.list({ search: search || undefined });
      setSuppliers(data.items || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load suppliers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => {
    const timer = setTimeout(fetchSuppliers, 300);
    return () => clearTimeout(timer);
  }, [fetchSuppliers]);

  const openNew = () => {
    setEditingSupplier(null);
    setForm({ supplier_name: '', supplier_code: '', contact_name: '', contact_email: '', contact_phone: '', address: '', notes: '' });
    setProductLinks([]);
    setDialogOpen(true);
  };

  const openEdit = async (s) => {
    setEditingSupplier(s);
    setForm({
      supplier_name: s.supplier_name || '',
      supplier_code: s.supplier_code || '',
      contact_name: s.contact_name || '',
      contact_email: s.contact_email || '',
      contact_phone: s.contact_phone || '',
      address: s.address || '',
      notes: s.notes || '',
      is_active: s.is_active,
    });
    // Fetch full supplier detail for product links
    try {
      const detail = await suppliersService.get(s.id);
      setProductLinks(detail.product_links || []);
    } catch {
      setProductLinks([]);
    }
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.supplier_name?.trim()) {
      toast({ title: 'Validation', description: 'Supplier name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingSupplier) {
        await suppliersService.update(editingSupplier.id, form);
        toast({ title: 'Success', description: 'Supplier updated' });
      } else {
        await suppliersService.create(form);
        toast({ title: 'Success', description: 'Supplier created' });
      }
      setDialogOpen(false);
      fetchSuppliers();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    setDeleting(id);
    try {
      await suppliersService.delete(id);
      toast({ title: 'Deleted', description: 'Supplier removed' });
      fetchSuppliers();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete'), variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  // Product link management
  const handleAddLink = async () => {
    if (!linkingProduct || !editingSupplier?.id) return;
    setAddingLink(true);
    try {
      await suppliersService.addProductLink(editingSupplier.id, {
        beverage_product_id: Number(linkingProduct),
        supplier_cost_price: linkCostPrice ? parseFloat(linkCostPrice) : null,
      });
      // Refresh links
      const detail = await suppliersService.get(editingSupplier.id);
      setProductLinks(detail.product_links || []);
      setLinkingProduct('');
      setLinkCostPrice('');
      toast({ title: 'Linked', description: 'Product linked to supplier' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to link product'), variant: 'destructive' });
    } finally {
      setAddingLink(false);
    }
  };

  const handleRemoveLink = async (linkId) => {
    if (!editingSupplier?.id) return;
    setRemovingLink(linkId);
    try {
      await suppliersService.removeProductLink(editingSupplier.id, linkId);
      setProductLinks(prev => prev.filter(l => l.id !== linkId));
      toast({ title: 'Unlinked', description: 'Product removed from supplier' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to unlink'), variant: 'destructive' });
    } finally {
      setRemovingLink(null);
    }
  };

  // Products not yet linked
  const unlinkedProducts = allProducts.filter(
    p => !productLinks.some(l => String(l.beverage_product_id) === String(p.id))
  );

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')}
            data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Truck className="h-6 w-6 text-slate-600" />
              Suppliers
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your beverage suppliers
            </p>
          </div>
        </div>
        <Button onClick={openNew} data-testid="add-supplier-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Supplier
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search suppliers..." value={search}
                onChange={(e) => setSearch(e.target.value)} className="pl-9"
                data-testid="supplier-search" />
            </div>
            <Badge variant="outline" className="whitespace-nowrap">
              {suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}
            </Badge>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Truck className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No suppliers found</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={openNew}>
                <Plus className="h-4 w-4 mr-1" /> Add your first supplier
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Code</TableHead>
                    <TableHead className="hidden md:table-cell">Contact</TableHead>
                    <TableHead className="hidden lg:table-cell">Email</TableHead>
                    <TableHead className="hidden lg:table-cell">Phone</TableHead>
                    <TableHead className="text-center">Products</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map((s) => (
                    <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                      <TableCell className="font-medium">{s.supplier_name}</TableCell>
                      <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">
                        {s.supplier_code || '-'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm">
                        {s.contact_name || '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                        {s.contact_email || '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                        {s.contact_phone || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-xs">
                          <Package className="h-3 w-3 mr-1" />
                          {s.product_link_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={s.is_active ? 'default' : 'secondary'} className="text-xs">
                          {s.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(s.id)} disabled={deleting === s.id}
                            data-testid={`delete-supplier-${s.id}`}>
                            {deleting === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Supplier Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              {editingSupplier ? 'Edit Supplier' : 'New Supplier'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Supplier Name *</Label>
                <Input value={form.supplier_name}
                  onChange={(e) => setForm(f => ({ ...f, supplier_name: e.target.value }))}
                  placeholder="e.g. Diageo" data-testid="supplier-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input value={form.supplier_code}
                  onChange={(e) => setForm(f => ({ ...f, supplier_code: e.target.value }))}
                  placeholder="e.g. DIA001" data-testid="supplier-code-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input value={form.contact_name}
                  onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))}
                  data-testid="supplier-contact-name" />
              </div>
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input type="email" value={form.contact_email}
                  onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))}
                  data-testid="supplier-contact-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Contact Phone</Label>
              <Input value={form.contact_phone}
                onChange={(e) => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                data-testid="supplier-contact-phone" />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Textarea value={form.address}
                onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                rows={2} data-testid="supplier-address" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2} data-testid="supplier-notes" />
            </div>
            {editingSupplier && (
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active !== false}
                  onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))}
                  data-testid="supplier-active-toggle" />
                <Label>Active</Label>
              </div>
            )}

            {/* Product Links Section (edit mode only) */}
            {editingSupplier && (
              <>
                <Separator />
                <div className="space-y-3">
                  <Label className="text-sm font-semibold flex items-center gap-1.5">
                    <Link2 className="h-4 w-4" /> Linked Products
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Link products to this supplier. Linked products will be prioritised when selecting products in Deliveries and Credits.
                  </p>

                  {/* Add link row */}
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground mb-1">Product</Label>
                      <ProductPicker
                        products={unlinkedProducts}
                        value={linkingProduct}
                        onChange={setLinkingProduct}
                        placeholder="Search product to link..."
                        testId="link-product-picker"
                      />
                    </div>
                    <div className="w-[100px]">
                      <Label className="text-xs text-muted-foreground mb-1">Cost ({currencySymbol})</Label>
                      <Input type="number" step="0.01" className="h-9 text-sm"
                        value={linkCostPrice}
                        onChange={(e) => setLinkCostPrice(e.target.value)}
                        placeholder="0.00"
                        data-testid="link-cost-input" />
                    </div>
                    <Button type="button" size="sm" onClick={handleAddLink}
                      disabled={!linkingProduct || addingLink}
                      data-testid="add-product-link-btn">
                      {addingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </div>

                  {/* Existing links */}
                  {productLinks.length > 0 && (
                    <div className="border rounded-md overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 text-xs">
                            <TableHead className="text-xs">Product</TableHead>
                            <TableHead className="text-xs w-[80px]">Code</TableHead>
                            <TableHead className="text-xs w-[80px]">Cost</TableHead>
                            <TableHead className="text-xs w-[40px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {productLinks.map(lk => (
                            <TableRow key={lk.id} data-testid={`product-link-${lk.id}`}>
                              <TableCell className="text-sm">{lk.product_name || `Product #${lk.beverage_product_id}`}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{lk.product_code || '-'}</TableCell>
                              <TableCell className="text-xs font-mono">
                                {lk.supplier_cost_price !== null ? `${currencySymbol}${lk.supplier_cost_price?.toFixed(2)}` : '-'}
                              </TableCell>
                              <TableCell>
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                                  onClick={() => handleRemoveLink(lk.id)}
                                  disabled={removingLink === lk.id}
                                  data-testid={`remove-link-${lk.id}`}>
                                  {removingLink === lk.id
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <X className="h-3.5 w-3.5 text-destructive" />}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  {productLinks.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-3">
                      No products linked yet. Use the picker above to link products.
                    </p>
                  )}
                </div>
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-supplier-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <Save className="h-4 w-4 mr-2" />
                {editingSupplier ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
